import { useEffect } from 'react'
import { db } from '../lib/db'
import { upsertPersonActivity } from '../lib/personActivity'
import type { CrmSyncCompletedBroadcast } from '../lib/crm'

// Subscribe to CRM_SYNC_COMPLETED broadcasts from the extension and:
//   1. Mirror crmMessageSync rows into Dexie so this page's useLiveQuery
//      consumers (unsynced badges, auto-sync gating) notice — the SW writes
//      raw IDB in its own origin which Dexie can't observe.
//   2. Forward runningTotals to Supabase via upsert_person_activity.
//
// The SW only emits this event when it ran a sync the web app didn't already
// reconcile inline (sidepanel direct path, SW auto-sync on native LinkedIn).
// useConversationSync passes notifyOnComplete:false so its own runs are
// suppressed. Mount once at the app shell. Drops on the floor if no tab is
// open — matches the useInviteSync precedent.
//
// Multi-tab idempotency: the broadcast reaches every FwdSlash content script,
// so each open tab's reconciler hears it. upsert_person_activity is additive
// (per its SQL contract), so without dedupe two tabs would double-count. We
// claim each syncId via Web Locks + localStorage so exactly one tab runs the
// reconcile per broadcast.
export function useCrmSyncReconciler() {
  useEffect(() => {
    pruneStaleClaimMarkers()

    const handler = async (event: MessageEvent) => {
      // Only trust same-window, same-origin postMessages. content-data.js
      // relays from chrome.runtime to window via postMessage; an iframe
      // could otherwise spoof a sync record + Supabase upsert.
      if (event.source !== window) return
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'CRM_SYNC_COMPLETED') return
      const report = event.data.report as CrmSyncCompletedBroadcast | undefined
      if (!report?.conversationUrn || !report?.crmName || !report?.syncId) return

      const claimed = await claimSyncId(report.syncId)
      if (!claimed) return

      if (report.syncedRecords?.length) {
        try {
          await db.crmMessageSync.bulkPut(report.syncedRecords)
        } catch (e) {
          console.warn('[CRM Sync Reconciler] Dexie mirror failed:', e)
        }
      }

      if (report.runningTotals && report.participantProfileUrn) {
        try {
          await upsertPersonActivity({
            profileUrn: report.participantProfileUrn,
            displayName: report.participantName,
            linkedinSlug: report.linkedinSlug,
            totals: report.runningTotals,
          })
          console.log(
            '[CRM Sync Reconciler] Supabase person activity upserted for',
            report.participantProfileUrn,
          )
        } catch (e) {
          console.warn('[CRM Sync Reconciler] Supabase upsert failed:', e)
        }
      }
    }

    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [])
}

const CLAIM_KEY_PREFIX = 'crmSyncReconcileSeen:'
const CLAIM_TTL_MS = 24 * 60 * 60 * 1000
const CLAIM_LOCK_NAME = 'crm-sync-reconcile-claim'

// Atomically claim a syncId across tabs. Web Locks serializes the
// check-and-mark; localStorage gives the cross-tab + cross-reload memory.
// Returns true if this tab won the claim, false if another tab already did.
// Falls back to localStorage-only when Web Locks isn't available — the race
// window narrows to microseconds, acceptable downside for the rare browser
// without lock support.
async function claimSyncId(syncId: string): Promise<boolean> {
  const key = `${CLAIM_KEY_PREFIX}${syncId}`
  const tryClaim = (): boolean => {
    try {
      if (localStorage.getItem(key)) return false
      localStorage.setItem(key, String(Date.now()))
      return true
    } catch {
      return true
    }
  }

  if (typeof navigator === 'undefined' || !navigator.locks) {
    return tryClaim()
  }
  try {
    return await navigator.locks.request(CLAIM_LOCK_NAME, tryClaim)
  } catch {
    return tryClaim()
  }
}

// Sweep claim markers older than CLAIM_TTL_MS. Run once at hook mount so a
// long-lived tab doesn't grow localStorage indefinitely. Bounded by the
// CLAIM_KEY_PREFIX scan so we don't touch unrelated keys.
function pruneStaleClaimMarkers() {
  try {
    const cutoff = Date.now() - CLAIM_TTL_MS
    const stale: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k || !k.startsWith(CLAIM_KEY_PREFIX)) continue
      const raw = localStorage.getItem(k)
      const ts = raw ? Number(raw) : 0
      if (!Number.isFinite(ts) || ts < cutoff) stale.push(k)
    }
    for (const k of stale) localStorage.removeItem(k)
  } catch {
    // private mode / quota / unavailable — non-fatal, claims still work.
  }
}
